import type { SendTimes } from "../../api/OutreachTouchesApi";

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** The slot the grid names best, in words (or why none is named yet). */
export function bestSlotText(st: SendTimes): string {
  if (!st.best) return `No slot has ${st.min_matured} or more requests that have had their full window yet, so none can be called best.`;
  const b = st.best;
  return `Best so far: ${WEEKDAYS[b.weekday]} ${st.bands[b.band]} (${b.rate}% accepted of ${b.matured} requests).`;
}

/** Weekday by time of day: the share of requests accepted, one hue (darker = more accepted). A slot with too few requests that have had
 *  their full window is shown grey with its count, so a lucky 2 of 2 does not look like a finding. Times are Karachi, when the send was marked. */
export default function SendTimeGrid({ sendTimes, windowDays = 7 }: { sendTimes: SendTimes; windowDays?: number }) {
  const { cells, bands, min_matured } = sendTimes;
  const byKey = new Map(cells.map((c) => [`${c.weekday}-${c.band}`, c]));
  const cols = bands.map((_, i) => i);      // every band of the day, so a time nobody has sent at yet (or one that starts to be used) is visible
  const days = WEEKDAYS.map((_, i) => i).filter((i) => i < 5 || cells.some((c) => c.weekday === i));
  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground" data-testid="best-slot">{bestSlotText(sendTimes)}</p>
      <div className="overflow-x-auto">
        <div role="table" aria-label="Acceptance rate by weekday and time of day" className="inline-grid gap-1 text-xs"
          style={{ gridTemplateColumns: `6.5rem repeat(${cols.length}, minmax(3.75rem, 1fr))` }}>
          <div role="row" className="contents">
            <span role="columnheader"><span className="sr-only">Day</span></span>
            {cols.map((i) => <span role="columnheader" key={i} className="text-center text-muted-foreground pb-1">{bands[i]}</span>)}
          </div>
          {days.map((d) => (
            <div role="row" className="contents" key={d}>
              <span role="rowheader" className="self-center text-muted-foreground">{WEEKDAYS[d]}</span>
              {cols.map((i) => {
                const c = byKey.get(`${d}-${i}`);
                if (!c) return <span role="cell" key={i} className="h-10 rounded-md bg-surface-muted/50" aria-label={`${WEEKDAYS[d]} ${bands[i]}: nothing sent`} />;
                const enough = c.matured >= min_matured && c.rate !== null;
                const label = `${WEEKDAYS[d]} ${bands[i]}: ${c.matured ? `${c.rate}% accepted of ${c.matured} that had their full window` : "none had their full window yet"} (${c.sent} sent in all)`;
                return (
                  <span role="cell" key={i} tabIndex={0} title={label} aria-label={label} data-testid={`slot-${d}-${i}`}
                    data-enough={enough}
                    className={`h-10 rounded-md flex flex-col items-center justify-center leading-tight ${enough ? "text-foreground" : "bg-surface-muted text-muted-foreground"}`}
                    style={enough ? { background: `hsl(var(--chart-2) / ${(0.12 + 0.7 * ((c.rate ?? 0) / 100)).toFixed(2)})` } : undefined}>
                    <strong className="font-semibold">{c.matured ? `${c.rate}%` : "n/a"}</strong>
                    <span className="text-[10px] opacity-80">n={c.matured}</span>
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Darker means a higher share accepted. Grey: fewer than {min_matured} requests with a full {windowDays}-day window, too few to read. Time is when the request was marked as sent (Karachi).</p>
    </div>
  );
}
